import { marketingDeltaConfidence } from './marketingCompareModel';
import {
  marketingNumber,
  MarketingTabId,
  MARKETING_NONE_KEY,
} from './marketingModel';
import { IMarketingCount, IMarketingMetrics } from './types';

/**
 * ТЗ 0103 — быстрые фильтры вкладок (`qf` адреса, design-erp §2, §3.3–3.4).
 *
 * Фильтр отбирает строки НА СЕРВЕРЕ до страниц (аргумент `having:
 * [MarketingRowPredicate!]` у `marketingBreakdown` / `marketingCreatives`,
 * design-backend §A4): фильтр одной страницы на фронте врал бы о числах.
 * Числа на чипах — `predicateCounts` ответа (до `having`, после минимумов).
 *
 * Исключение — «Курсы»: курсов единицы, и все они приходят ОДНОЙ страницей
 * (`limit 200`), поэтому их фильтры считаются на фронте по строкам и
 * `previous` строки (`marketingCourseQuickFilter`) — это честно, страница и
 * есть всё множество. Серверного предиката для «растут / падают / спрос
 * без продаж» нет вовсе; «без продаж» — то же правило, что `NO_SALES`.
 */

/** Серверный предикат строки (`enum MarketingRowPredicate`). */
export const MARKETING_ROW_PREDICATES = [
  'WITH_SALES',
  'NO_SALES',
  'LEADS_NO_SALES',
  'CONTRACTS_UNSIGNED',
  'SPEND_NO_LEADS',
] as const;

export type MarketingRowPredicate = typeof MARKETING_ROW_PREDICATES[number];

export interface IMarketingQuickFilter {
  /** Значение `qf` адреса. */
  id: string;
  /** Предикат сервера; `null` — «Все» или отбор без предиката. */
  predicate: MarketingRowPredicate | null;
}

/** Первый фильтр вкладки — «Все» (без параметра в адресе). */
export const MARKETING_QUICK_FILTER_ALL = 'all';

/**
 * Быстрые фильтры по вкладкам — взаимоисключающие; независимые
 * переключатели («Выгорают», «Только активные») — отдельными параметрами.
 */
export const MARKETING_QUICK_FILTERS: Readonly<
  Partial<Record<MarketingTabId, ReadonlyArray<IMarketingQuickFilter>>>
> = {
  ads: [
    { id: MARKETING_QUICK_FILTER_ALL, predicate: null },
    { id: 'withSales', predicate: 'WITH_SALES' },
    { id: 'noSales', predicate: 'NO_SALES' },
    { id: 'leadsNoSales', predicate: 'LEADS_NO_SALES' },
    { id: 'contractsUnsigned', predicate: 'CONTRACTS_UNSIGNED' },
    { id: 'spendNoLeads', predicate: 'SPEND_NO_LEADS' },
  ],
  courses: [
    { id: MARKETING_QUICK_FILTER_ALL, predicate: null },
    { id: 'growing', predicate: null },
    { id: 'falling', predicate: null },
    { id: 'demandLowSales', predicate: null },
    { id: 'noSales', predicate: 'NO_SALES' },
  ],
  // ТЗ 0103, этап 4: SEO › «Что написать» — вид разрыва (`kind` сервера,
  // не `having`): страницы нет, страница ниже 10-й, курса нет, вопросы.
  seo: [
    { id: MARKETING_QUICK_FILTER_ALL, predicate: null },
    { id: 'no_page', predicate: null },
    { id: 'weak_page', predicate: null },
    { id: 'no_course', predicate: null },
    { id: 'questions', predicate: null },
  ],
};

/** Сервер принимает `having` (этап 2 ТЗ 0103): чипы «Рекламы» живые. */
export const MARKETING_QUICK_FILTERS_LIVE = true;

/** Вкладки, чьи быстрые фильтры считает фронт (вся выдача — одна страница). */
export const MARKETING_QUICK_FILTERS_CLIENT_TABS: ReadonlyArray<MarketingTabId> = [
  'courses',
];

/** Ключ «все строки» в `predicateCounts` сервера. */
export const MARKETING_PREDICATE_ALL = 'ALL';

/**
 * Числа чипов вкладки из `predicateCounts` сервера: id фильтра → число;
 * «Все» — `ALL`. Нет ответа — `null` у всех (число не знаем).
 */
export const marketingQuickFilterCounts = (
  tab: MarketingTabId,
  counts: ReadonlyArray<IMarketingCount> | null | undefined,
): Record<string, number | null> => {
  const byValue = new Map(
    (counts ?? []).map((item) => [item.value, item.count]),
  );
  return marketingQuickFiltersOf(tab).reduce<Record<string, number | null>>(
    (acc, item) => {
      const key = item.predicate ?? MARKETING_PREDICATE_ALL;
      const value =
        item.id === MARKETING_QUICK_FILTER_ALL || item.predicate
          ? byValue.get(key)
          : undefined;
      acc[item.id] = counts && value !== undefined ? value : null;
      return acc;
    },
    {},
  );
};

/* ── «Курсы»: фильтры на фронте ──────────────────────────────────────── */

/** Строка курса для фильтра: показатели окна и окна сравнения. */
export interface IMarketingCourseFilterRow {
  /** Slug курса; «Курс не определён» (`MARKETING_NONE_KEY`) — не курс. */
  key?: string;
  metrics: IMarketingMetrics;
  previous?: IMarketingMetrics | null;
}

/**
 * Растёт / падает ли курс (финальная проверка 0103, F-E4): только у
 * настоящего курса (не «Курс не определён») с базой не меньше 10 CRM-лидов
 * в одном из окон (`marketingDeltaConfidence`): 1 → 3 — не «рост на 200 %».
 * Без сравнения — `null`.
 */
export const marketingCourseLeadsTrend = (
  row: IMarketingCourseFilterRow,
): 'growing' | 'falling' | null => {
  const { key, metrics, previous } = row;
  if (!previous || key === MARKETING_NONE_KEY) return null;
  const leads = metrics.crmLeads;
  const before = previous.crmLeads;
  if (marketingDeltaConfidence(leads, before) !== 'ok') return null;
  if (leads > before) return 'growing';
  if (leads < before) return 'falling';
  return null;
};

/**
 * «Есть спрос, мало продаж»: лидов не меньше `MARKETING_COURSE_DEMAND_MIN`, а
 * конверсия лид → продажа меньше половины общей по окну (у общей нет лидов
 * — меньше 2 %). «Спрос» здесь — CRM-лиды курса: частотность рынка
 * (DataForSEO) придёт с SEO-этапом.
 */
export const MARKETING_COURSE_DEMAND_MIN = 10;
export const MARKETING_COURSE_LOW_SALES_SHARE = 0.5;
export const MARKETING_COURSE_LOW_SALES_FLOOR = 0.02;

const salesOf = (metrics: IMarketingMetrics) =>
  marketingNumber(metrics.sales) ?? 0;

const spendOf = (metrics: IMarketingMetrics) =>
  marketingNumber(metrics.spendPln ?? metrics.spendCoveredPln ?? null) ?? 0;

/**
 * Подходит ли строка курса под фильтр (правила — в шапке модуля):
 *  - «Растут» / «Падают» — CRM-лиды против окна сравнения (без сравнения —
 *    не подходит ни одна: сравнивать не с чем); только курсы с базой ≥ 10
 *    лидов и не «Курс не определён» (`marketingCourseLeadsTrend`) — числа
 *    чипов равны фразе вывода;
 *  - «Есть спрос, мало продаж» — см. `MARKETING_COURSE_DEMAND_MIN`;
 *  - «Без продаж» — правило `NO_SALES` сервера: продаж нет, а расход, лиды
 *    или договоры есть.
 */
export const marketingCourseQuickFilter = (
  id: string,
  row: IMarketingCourseFilterRow,
  overallConversion: number | null,
): boolean => {
  const { metrics } = row;
  const leads = metrics.crmLeads;
  const sales = salesOf(metrics);
  switch (id) {
    case 'growing':
    case 'falling':
      return marketingCourseLeadsTrend(row) === id;
    case 'demandLowSales': {
      if (leads < MARKETING_COURSE_DEMAND_MIN) return false;
      const bar =
        overallConversion !== null && overallConversion > 0
          ? overallConversion * MARKETING_COURSE_LOW_SALES_SHARE
          : MARKETING_COURSE_LOW_SALES_FLOOR;
      return sales / leads < bar;
    }
    case 'noSales':
      return (
        sales === 0 &&
        (spendOf(metrics) > 0 || leads > 0 || (metrics.contractsSent ?? 0) > 0)
      );
    default:
      return true;
  }
};

/** Числа чипов «Курсов» — по строкам страницы (вся выдача). */
export const marketingCourseQuickFilterCounts = (
  rows: ReadonlyArray<IMarketingCourseFilterRow>,
  overallConversion: number | null,
): Record<string, number> =>
  marketingQuickFiltersOf('courses').reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.id] =
        item.id === MARKETING_QUICK_FILTER_ALL
          ? rows.length
          : rows.filter((row) =>
              marketingCourseQuickFilter(item.id, row, overallConversion),
            ).length;
      return acc;
    },
    {},
  );

/** Все id быстрых фильтров — для перевода (`marketing.quickFilters.<id>`). */
export const MARKETING_QUICK_FILTER_IDS: string[] = Array.from(
  new Set(
    Object.values(MARKETING_QUICK_FILTERS).flatMap((list) =>
      (list ?? []).map((item) => item.id),
    ),
  ),
);

/** Фильтры вкладки; у вкладки без быстрых фильтров — пусто. */
export const marketingQuickFiltersOf = (
  tab: MarketingTabId,
): ReadonlyArray<IMarketingQuickFilter> => MARKETING_QUICK_FILTERS[tab] ?? [];

/** Годится ли `qf` адреса для вкладки («Все» — не значение адреса). */
export const isMarketingQuickFilter = (
  tab: MarketingTabId,
  id: string | null | undefined,
): boolean =>
  Boolean(id) &&
  id !== MARKETING_QUICK_FILTER_ALL &&
  marketingQuickFiltersOf(tab).some((item) => item.id === id);

/**
 * `having` запроса вкладки: предикат выбранного фильтра; «Все», незнакомый,
 * фильтр без предиката и вкладка, чьи фильтры считает фронт («Курсы»), —
 * `null` (аргумент не уходит).
 */
export const marketingQuickFilterHaving = (
  tab: MarketingTabId,
  id: string | null | undefined,
): MarketingRowPredicate[] | null => {
  if (MARKETING_QUICK_FILTERS_CLIENT_TABS.includes(tab)) return null;
  if (!isMarketingQuickFilter(tab, id)) return null;
  const predicate =
    marketingQuickFiltersOf(tab).find((item) => item.id === id)?.predicate ??
    null;
  return predicate ? [predicate] : null;
};
